import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";

const NAVY = "#1B3A5C";
const TEAL = "#2A9D8F";

type AuthTab = "signin" | "signup";

type AuthModalProps = {
  open: boolean;
  onClose: () => void;
};

export default function AuthModal({ open, onClose }: AuthModalProps) {
  const [tab, setTab] = useState<AuthTab>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [passwordResetView, setPasswordResetView] = useState<"none" | "form" | "sent">("none");
  const [resetEmail, setResetEmail] = useState("");

  useEffect(() => {
    if (!open) {
      setError(null);
      setLoading(false);
      setEmail("");
      setPassword("");
      setTab("signin");
      setSignUpSuccess(false);
      setPasswordResetView("none");
      setResetEmail("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    await supabase.auth.getSession();
    onClose();
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: err } = await supabase.auth.signUp({ email, password });
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSignUpSuccess(true);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: "https://www.floridasirs.org/app",
    });
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setPasswordResetView("sent");
  };

  const switchTab = (t: AuthTab) => {
    setTab(t);
    setError(null);
    setSignUpSuccess(false);
    setPasswordResetView("none");
    setResetEmail("");
  };

  const btnStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 16px",
    backgroundColor: TEAL,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 15,
    cursor: loading ? "not-allowed" : "pointer",
    opacity: loading ? 0.7 : 1,
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    border: `1px solid #D2DEE9`,
    borderRadius: 6,
    fontSize: 15,
    marginBottom: 14,
    boxSizing: "border-box",
  };

  const linkButtonStyle: React.CSSProperties = {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: TEAL,
    textDecoration: "underline",
    fontSize: 14,
    fontWeight: 500,
  };

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(27, 58, 92, 0.45)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        style={{
          backgroundColor: "#fff",
          borderRadius: 12,
          maxWidth: 420,
          width: "100%",
          overflow: "hidden",
          boxShadow: "0 16px 48px rgba(27,58,92,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          id="auth-modal-title"
          style={{
            backgroundColor: NAVY,
            color: "#fff",
            padding: "16px 20px",
            fontSize: 18,
            fontWeight: 700,
          }}
        >
          Account
        </div>

        {signUpSuccess ? (
          <div style={{ padding: 24 }}>
            <p style={{ fontSize: 15, lineHeight: 1.55, color: NAVY, marginBottom: 20 }}>
              ✅ Check your inbox — we&apos;ve sent you a confirmation email. Click the link in the email to activate
              your account, then come back to sign in.
            </p>
            <button
              type="button"
              onClick={() => {
                setSignUpSuccess(false);
                setTab("signin");
                setPassword("");
                setError(null);
              }}
              style={btnStyle}
            >
              Back to Sign In
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                marginTop: 12,
                width: "100%",
                padding: 10,
                background: "transparent",
                border: "none",
                color: "#64748B",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Cancel
            </button>
          </div>
        ) : passwordResetView === "sent" ? (
          <div style={{ padding: 24 }}>
            <p style={{ fontSize: 15, lineHeight: 1.55, color: NAVY, marginBottom: 20 }}>Reset link sent — check your email.</p>
            <button
              type="button"
              onClick={() => {
                setPasswordResetView("none");
                setResetEmail("");
                setError(null);
              }}
              style={linkButtonStyle}
            >
              Back to Sign In
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                marginTop: 16,
                width: "100%",
                padding: 10,
                background: "transparent",
                border: "none",
                color: "#64748B",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Cancel
            </button>
          </div>
        ) : passwordResetView === "form" ? (
          <form onSubmit={handleResetPassword} style={{ padding: 24 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: NAVY, marginBottom: 16 }}>Reset your password</h2>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: NAVY, marginBottom: 6 }}>Email</label>
            <input
              type="email"
              autoComplete="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
              style={inputStyle}
            />
            {error && <p style={{ color: "#B91C1C", fontSize: 14, marginBottom: 12, marginTop: -6 }}>{error}</p>}
            <button type="submit" disabled={loading} style={btnStyle}>
              {loading ? "Please wait…" : "Send reset link"}
            </button>
            <button
              type="button"
              onClick={() => {
                setPasswordResetView("none");
                setResetEmail("");
                setError(null);
              }}
              style={{ ...linkButtonStyle, display: "block", width: "100%", textAlign: "center", marginTop: 16 }}
            >
              Back to Sign In
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                marginTop: 12,
                width: "100%",
                padding: 10,
                background: "transparent",
                border: "none",
                color: "#64748B",
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              Cancel
            </button>
          </form>
        ) : (
          <>
            <div style={{ display: "flex", borderBottom: `1px solid #E2E8F0` }}>
              {(["signin", "signup"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => switchTab(t)}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    border: "none",
                    background: tab === t ? "#fff" : "#F3F7FB",
                    color: tab === t ? NAVY : "#64748B",
                    fontWeight: tab === t ? 700 : 600,
                    fontSize: 14,
                    cursor: "pointer",
                    borderBottom: tab === t ? `3px solid ${TEAL}` : "3px solid transparent",
                  }}
                >
                  {t === "signin" ? "Sign In" : "Sign Up"}
                </button>
              ))}
            </div>
            <form onSubmit={tab === "signin" ? handleSignIn : handleSignUp} style={{ padding: 24 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: NAVY, marginBottom: 6 }}>Email</label>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={inputStyle}
              />
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: NAVY, marginBottom: 6 }}>Password</label>
              <input
                type="password"
                autoComplete={tab === "signin" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                style={{ ...inputStyle, marginBottom: tab === "signin" ? 8 : 14 }}
              />
              {tab === "signin" && (
                <div style={{ marginBottom: 14 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setPasswordResetView("form");
                      setResetEmail(email);
                      setError(null);
                    }}
                    style={linkButtonStyle}
                  >
                    Forgot your password?
                  </button>
                </div>
              )}
              {error && (
                <p style={{ color: "#B91C1C", fontSize: 14, marginBottom: 12, marginTop: -6 }}>{error}</p>
              )}
              <button type="submit" disabled={loading} style={btnStyle}>
                {loading ? "Please wait…" : tab === "signin" ? "Sign In" : "Sign Up"}
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  marginTop: 12,
                  width: "100%",
                  padding: 10,
                  background: "transparent",
                  border: "none",
                  color: "#64748B",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
